import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import IconButton from '@material-ui/core/IconButton';
import CloudDownloadOutlined from '@material-ui/icons/CloudDownloadOutlined';
import CheckCircleOutlined from '@material-ui/icons/CheckCircleOutlined';
import ErrorOutlineOutlined from '@material-ui/icons/ErrorOutlineOutlined';
import ScheduleOutlined from '@material-ui/icons/ScheduleOutlined';
import GetAppOutlined from '@material-ui/icons/GetAppOutlined';
import ReplayOutlined from '@material-ui/icons/ReplayOutlined';
import CloseOutlined from '@material-ui/icons/CloseOutlined';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import TextButton from '../buttons/TextButton';
import {SITUACAO_DOWNLOAD, emAndamento, ativo, textoSituacao} from './downloadSegundoPlano.js';

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
    },
    icone: {
        marginTop: 2,
    },
    iconeAndamento: {
        color: theme.palette.primary.main,
    },
    iconeConcluido: {
        color: theme.palette.success ? theme.palette.success.main : theme.palette.primary.main,
    },
    iconeErro: {
        color: theme.palette.error.main,
    },
    iconeFila: {
        color: theme.palette.text.secondary,
    },
    conteudo: {
        flex: 1,
        minWidth: 0,
    },
    titulo: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        overflowWrap: 'anywhere',
    },
    texto: {
        overflowWrap: 'anywhere',
    },
    textoErro: {
        color: theme.palette.error.main,
    },
    progresso: {
        marginTop: theme.spacing(1.5),
    },
    acoes: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: theme.spacing(1),
    },
}));

// Item de um download na lista do painel (fila, gerando, baixando, concluído ou com erro).
export default function ItemDownloadSegundoPlano(props) {
    const {download, onBaixar, onTentarNovamente, onCancelar, onRemover} = props;
    const classes = useStyles();

    const andamento = emAndamento(download.situacao);
    const pendente = ativo(download.situacao);
    const concluido = download.situacao === SITUACAO_DOWNLOAD.CONCLUIDO;
    const erro = download.situacao === SITUACAO_DOWNLOAD.ERRO;
    const fila = download.situacao === SITUACAO_DOWNLOAD.NA_FILA;

    const botaoRemover = (
        <IconButton size='small' aria-label='Remover' onClick={onRemover}>
            <CloseOutlined fontSize='small'/>
        </IconButton>
    );

    return (
        <div className={classes.root}>
            <div className={classes.cabecalho}>
                <span className={classes.icone}>
                    {erro
                        ? <ErrorOutlineOutlined className={classes.iconeErro}/>
                        : concluido
                            ? <CheckCircleOutlined className={classes.iconeConcluido}/>
                            : fila
                                ? <ScheduleOutlined className={classes.iconeFila}/>
                                : <CloudDownloadOutlined className={classes.iconeAndamento}/>}
                </span>
                <div className={classes.conteudo}>
                    <Typography variant='subtitle2' className={classes.titulo}>
                        {download.titulo}
                    </Typography>
                    {!!download.descricao && (
                        <Typography variant='body2' color='textSecondary' className={classes.texto}>
                            {download.descricao}
                        </Typography>
                    )}
                    <Typography
                        variant='body2'
                        aria-live='polite'
                        className={clsx(classes.texto, erro && classes.textoErro)}>
                        {textoSituacao(download)}
                    </Typography>
                </div>
            </div>
            {andamento && (
                <LinearProgress
                    className={classes.progresso}
                    {...(download.progresso == null
                        ? {variant: 'indeterminate'}
                        : {variant: 'determinate', value: download.progresso})}/>
            )}
            {pendente && (
                <div className={classes.acoes}>
                    <TextButton size='small' onClick={onCancelar}>Cancelar</TextButton>
                </div>
            )}
            {concluido && (
                <div className={classes.acoes}>
                    <TextButton size='small' startIcon={<GetAppOutlined/>} onClick={onBaixar}>
                        Baixar novamente
                    </TextButton>
                    {botaoRemover}
                </div>
            )}
            {erro && (
                <div className={classes.acoes}>
                    <TextButton size='small' startIcon={<ReplayOutlined/>} onClick={onTentarNovamente}>
                        Tentar novamente
                    </TextButton>
                    {botaoRemover}
                </div>
            )}
        </div>
    );
}

ItemDownloadSegundoPlano.propTypes = {
    download: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        titulo: PropTypes.string,
        descricao: PropTypes.string,
        nomeArquivo: PropTypes.string,
        situacao: PropTypes.string.isRequired,
        progresso: PropTypes.number,
        mensagemErro: PropTypes.string,
    }).isRequired,
    onBaixar: PropTypes.func.isRequired,
    onTentarNovamente: PropTypes.func.isRequired,
    onCancelar: PropTypes.func.isRequired,
    onRemover: PropTypes.func.isRequired,
};
